/**
 * Self-Improving Workflow Demo — fixtures.
 *
 * THROWAWAY DEMO CODE. Builds the dataset the resolver injects into the REAL
 * /workflows pages: a curated Definitions list (the seeded failing "Weekly
 * overdue-invoice reminders" workflow + a couple of healthy ones), plus the
 * failing workflow's lineage/definition/runs. The recovery copy (root cause,
 * replay totals, pass rate) drives the on-page "Investigate failures" sequence.
 * Delete alongside frontend/src/demo/workflow-recovery.
 *
 * IDs are fixed UUIDs so the workflow reads as a normal one — the address bar
 * shows `/workflows/<uuid>`. This data is injected client-side (never round-
 * tripped through the server), so nothing re-validates it.
 */

import type {
  ListWorkflowDefinitionSummariesResponse,
  ListWorkflowRunsForDefinitionResponse,
  WorkflowDefinitionResponse,
  WorkflowDefinitionSummary,
  WorkflowResponse,
  WorkflowRunResponse,
} from '@erp/rpc';

export const DEMO_WORKFLOW_ID = 'a1f4c2e0-1b3d-4c5a-9e7f-2d4b6a8c0e10';
export const DEMO_DEFINITION_ID = 'a1f4c2e0-1b3d-4c5a-9e7f-2d4b6a8c0e20';
const DEMO_EMAIL_STEP_ID = 'a1f4c2e0-1b3d-4c5a-9e7f-2d4b6a8c0e30';
const DEMO_ORG_ID = 'a1f4c2e0-1b3d-4c5a-9e7f-2d4b6a8c0e40';
const DEMO_USER_ID = 'a1f4c2e0-1b3d-4c5a-9e7f-2d4b6a8c0e50';

/** Narrative knobs for the scene — referenced by the fixtures + the overview. */
export const DEMO = {
  name: 'Weekly overdue-invoice reminders',
  description:
    'Every Monday, email each customer with unpaid invoices a statement of outstanding invoices, with overdue lines bolded and highlighted.',
  failingStepName: 'Email statement to customer',
  failureCount: 23,
  totalRuns: 412,
  failureWindow: 'the last 30 days',
  rootCause:
    'The email step throws when a customer has no billing email on file — every failed run hit a missing or malformed recipient address.',
  fixNodeTitle: 'Validate billing email',
  fixNodeSubtitle: 'Skip & flag customers with no valid email on file',
  replayTotalCases: 4217,
  passRate: '99.8%',
  flaggedCases: 8,
  currentVersion: 3,
  newVersion: 4,
} as const;

const HOUR_MS = 60 * 60 * 1000;
const iso = (msAgo: number): string =>
  new Date(Date.now() - msAgo).toISOString();

/** Build a stable UUID-shaped run id from an index (keeps run-detail URLs clean). */
const runId = (n: number): string =>
  `a1f4c2e0-1b3d-4c5a-9e7f-${n.toString().padStart(12, '0')}`;

/** The whole dataset the resolver serves. */
export interface WorkflowRecoveryDataset {
  summaries: WorkflowDefinitionSummary[];
  workflowsById: Record<string, WorkflowResponse>;
  definitionsById: Record<string, WorkflowDefinitionResponse>;
  runsByDefId: Record<string, ListWorkflowRunsForDefinitionResponse>;
}

// ---------------------------------------------------------------------------
// The failing workflow (the scene).
// ---------------------------------------------------------------------------
function buildFailingRuns(): WorkflowRunResponse[] {
  const runs: WorkflowRunResponse[] = [];

  // 23 failed runs, most recent first, roughly one per ~28h over ~30 days.
  for (let i = 0; i < DEMO.failureCount; i++) {
    const triggeredMsAgo = (i + 1) * 28 * HOUR_MS;
    runs.push({
      id: runId(i + 1),
      workflowDefinitionId: DEMO_DEFINITION_ID,
      workflowId: DEMO_WORKFLOW_ID,
      workflowDefinitionVersion: DEMO.currentVersion,
      workflowDefinitionIsPinned: true,
      workflowDefinitionChangelog: null,
      status: 'failed',
      triggeredBy: null,
      triggeredAt: iso(triggeredMsAgo),
      startedAt: iso(triggeredMsAgo - 1000),
      completedAt: iso(triggeredMsAgo - 4200),
      stopReason:
        'Email step failed: customer has no billing email on file (TypeError: recipient address is undefined).',
      errorStepId: DEMO_EMAIL_STEP_ID,
      totalSteps: 4,
      completedSteps: 3,
      wallTimeMs: 4200,
      userTimeMs: 3100,
      systemTimeMs: 1100,
      hasBeenRerun: false,
    });
  }

  // A handful of earlier successful runs (before the failures started).
  for (let i = 0; i < 6; i++) {
    const triggeredMsAgo = (DEMO.failureCount + i + 1) * 28 * HOUR_MS;
    runs.push({
      id: runId(100 + i + 1),
      workflowDefinitionId: DEMO_DEFINITION_ID,
      workflowId: DEMO_WORKFLOW_ID,
      workflowDefinitionVersion: DEMO.currentVersion,
      workflowDefinitionIsPinned: true,
      workflowDefinitionChangelog: null,
      status: 'completed',
      triggeredBy: null,
      triggeredAt: iso(triggeredMsAgo),
      startedAt: iso(triggeredMsAgo - 1000),
      completedAt: iso(triggeredMsAgo - 9000),
      totalSteps: 4,
      completedSteps: 4,
      wallTimeMs: 8000,
      userTimeMs: 6400,
      systemTimeMs: 1600,
      hasBeenRerun: false,
    });
  }

  return runs;
}

function buildFailingSummary(
  lastRun: WorkflowRunResponse,
): WorkflowDefinitionSummary {
  return {
    id: DEMO_WORKFLOW_ID,
    name: DEMO.name,
    description: DEMO.description,
    tags: ['accounts-receivable', 'reminders'],
    isActive: true,
    orchestratorType: 'temporal',
    createdBy: DEMO_USER_ID,
    createdByName: 'Nami',
    createdAt: iso(40 * 24 * HOUR_MS),
    updatedAt: iso(2 * HOUR_MS),
    deletedAt: null,
    nodeCount: 4,
    lastRun,
    pinnedVersionId: DEMO_DEFINITION_ID,
    pinnedVersionNumber: DEMO.currentVersion,
    pinnedVersionChangelog: 'Bold + highlight overdue invoice lines',
    draftCount: 0,
    versionCount: DEMO.currentVersion,
  };
}

function buildFailingWorkflow(): WorkflowResponse {
  return {
    id: DEMO_WORKFLOW_ID,
    organizationId: DEMO_ORG_ID,
    name: DEMO.name,
    description: DEMO.description,
    tags: ['accounts-receivable', 'reminders'],
    isActive: true,
    isComponent: false,
    exposedInputs: null,
    exposedOutputs: null,
    orchestratorType: 'temporal',
    fanoutStrategy: 'parallel',
    retryConfig: null,
    workflowManagerAgentDefinitionId: null,
    pinnedVersionId: DEMO_DEFINITION_ID,
    createdBy: DEMO_USER_ID,
    createdByName: 'Nami',
    createdAt: iso(40 * 24 * HOUR_MS),
    updatedAt: iso(2 * HOUR_MS),
  };
}

function buildFailingDefinition(): WorkflowDefinitionResponse {
  return {
    id: DEMO_DEFINITION_ID,
    workflowId: DEMO_WORKFLOW_ID,
    parentVersionId: null,
    version: DEMO.currentVersion,
    status: 'finalized',
    isPinned: true,
    changelog: 'Bold + highlight overdue invoice lines in the statement PDF',
    createdBy: DEMO_USER_ID,
    // The real graph isn't fabricated — the demo Overview tab renders its own
    // canvas for this workflow — so an empty step/edge set is fine here.
    steps: [],
    edges: [],
    createdAt: iso(40 * 24 * HOUR_MS),
    updatedAt: iso(2 * HOUR_MS),
  };
}

// ---------------------------------------------------------------------------
// A couple of healthy workflows so the curated list reads like a real one.
// They're list-only context; opening one shows a valid (empty) workflow.
// ---------------------------------------------------------------------------
interface Healthy {
  workflowId: string;
  definitionId: string;
  name: string;
  tags: string[];
  nodeCount: number;
  ranMsAgo: number;
}

const HEALTHY: Healthy[] = [
  {
    workflowId: 'a1f4c2e0-1b3d-4c5a-9e7f-2d4b6a8c0e60',
    definitionId: 'a1f4c2e0-1b3d-4c5a-9e7f-2d4b6a8c0e61',
    name: 'AP invoice ingestion',
    tags: ['accounts-payable'],
    nodeCount: 7,
    ranMsAgo: 5 * HOUR_MS,
  },
  {
    workflowId: 'a1f4c2e0-1b3d-4c5a-9e7f-2d4b6a8c0e70',
    definitionId: 'a1f4c2e0-1b3d-4c5a-9e7f-2d4b6a8c0e71',
    name: 'OMS order routing',
    tags: ['oms'],
    nodeCount: 9,
    ranMsAgo: 1 * HOUR_MS,
  },
];

function healthyRun(h: Healthy): WorkflowRunResponse {
  return {
    id: `${h.definitionId}-run`,
    workflowDefinitionId: h.definitionId,
    workflowId: h.workflowId,
    workflowDefinitionVersion: 2,
    workflowDefinitionIsPinned: true,
    workflowDefinitionChangelog: null,
    status: 'completed',
    triggeredBy: null,
    triggeredAt: iso(h.ranMsAgo),
    startedAt: iso(h.ranMsAgo - 1000),
    completedAt: iso(h.ranMsAgo - 6000),
    totalSteps: h.nodeCount,
    completedSteps: h.nodeCount,
    hasBeenRerun: false,
  };
}

function healthySummary(h: Healthy): WorkflowDefinitionSummary {
  return {
    id: h.workflowId,
    name: h.name,
    description: null,
    tags: h.tags,
    isActive: true,
    orchestratorType: 'temporal',
    createdBy: DEMO_USER_ID,
    createdByName: 'Nami',
    createdAt: iso(60 * 24 * HOUR_MS),
    updatedAt: iso(h.ranMsAgo),
    deletedAt: null,
    nodeCount: h.nodeCount,
    lastRun: healthyRun(h),
    pinnedVersionId: h.definitionId,
    pinnedVersionNumber: 2,
    draftCount: 0,
    versionCount: 2,
  };
}

function healthyWorkflow(h: Healthy): WorkflowResponse {
  return {
    id: h.workflowId,
    organizationId: DEMO_ORG_ID,
    name: h.name,
    description: null,
    tags: h.tags,
    isActive: true,
    isComponent: false,
    exposedInputs: null,
    exposedOutputs: null,
    orchestratorType: 'temporal',
    fanoutStrategy: 'parallel',
    retryConfig: null,
    workflowManagerAgentDefinitionId: null,
    pinnedVersionId: h.definitionId,
    createdBy: DEMO_USER_ID,
    createdByName: 'Nami',
    createdAt: iso(60 * 24 * HOUR_MS),
    updatedAt: iso(h.ranMsAgo),
  };
}

function healthyDefinition(h: Healthy): WorkflowDefinitionResponse {
  return {
    id: h.definitionId,
    workflowId: h.workflowId,
    parentVersionId: null,
    version: 2,
    status: 'finalized',
    isPinned: true,
    changelog: null,
    createdBy: DEMO_USER_ID,
    steps: [],
    edges: [],
    createdAt: iso(60 * 24 * HOUR_MS),
    updatedAt: iso(h.ranMsAgo),
  };
}

/** Build the full dataset (called by the demo store seed). */
export function buildWorkflowRecoveryDataset(): WorkflowRecoveryDataset {
  const failingRuns = buildFailingRuns();
  const failingSummary = buildFailingSummary(failingRuns[0]);

  const workflowsById: Record<string, WorkflowResponse> = {
    [DEMO_WORKFLOW_ID]: buildFailingWorkflow(),
  };
  const definitionsById: Record<string, WorkflowDefinitionResponse> = {
    [DEMO_DEFINITION_ID]: buildFailingDefinition(),
  };
  const runsByDefId: Record<string, ListWorkflowRunsForDefinitionResponse> = {
    [DEMO_DEFINITION_ID]: { runs: failingRuns, total: DEMO.totalRuns },
  };

  for (const h of HEALTHY) {
    workflowsById[h.workflowId] = healthyWorkflow(h);
    definitionsById[h.definitionId] = healthyDefinition(h);
    runsByDefId[h.definitionId] = {
      runs: [healthyRun(h)],
      total: 1,
    };
  }

  return {
    // Failing workflow first so it stands out at the top of the list.
    summaries: [failingSummary, ...HEALTHY.map(healthySummary)],
    workflowsById,
    definitionsById,
    runsByDefId,
  };
}
